import * as React from 'react';

import {
    getDefaultDayBudgetValue,
    validateDayBudgetValue
} from '../../service/DayBudget/DayBudget';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import {
    DayBudgetValue,
    RawDayBudgetValue,
    WalletDayBudgetValue
} from '../../typings/budget';
import { ValidationResult } from '../../typings/common';
import { DailyBudgetLimit } from '../../typings/constants';
import { CurrencyCode } from '../../typings/graphql-grid';
import { DayBudgetEditPopup } from '../DayBudgetEditPopup/DayBudgetEditPopup';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';

export interface ControlledDayBudgetEditPopupProps {
    campaignId: string;
    value?: DayBudgetValue;
    walletBudget?: WalletDayBudgetValue;
    currency: CurrencyCode;
    onSaveSuccess: () => void;
    saveDayBudget: (value: DayBudgetValue, campaignId: string) => void;
    isSaving: boolean;
    onCancel: () => void;
    isPopupOpened: boolean;
    dailyBudgetLimit: DailyBudgetLimit;
    error?: string;
}

interface ControlledDayBudgetEditPopupState {
    editPopupValue: RawDayBudgetValue;
    validationInfo?: ValidationResult;
}

export class ControlledDayBudgetEditPopup extends React.Component<
    ControlledDayBudgetEditPopupProps,
    ControlledDayBudgetEditPopupState
> {
    constructor(props: ControlledDayBudgetEditPopupProps) {
        super(props);

        this.state = {
            editPopupValue: this.toRawDayBudgetValue(
                props.value || getDefaultDayBudgetValue()
            )
        };
    }

    componentWillReceiveProps(nextProps: ControlledDayBudgetEditPopupProps) {
        const { value, isPopupOpened } = this.props;
        const {
            value: nextValue,
            isPopupOpened: nextIsPopupOpened,
            error
        } = nextProps;

        if (nextValue !== value || (isPopupOpened && !nextIsPopupOpened)) {
            this.setState({
                editPopupValue: this.toRawDayBudgetValue(
                    nextValue || getDefaultDayBudgetValue()
                ),
                validationInfo: undefined
            });
        }

        if (error) {
            this.setState({
                validationInfo: {
                    valid: false,
                    errors: [
                        {
                            type: 'server',
                            text: error,
                            path: 'common'
                        }
                    ]
                }
            });
        }
    }

    render() {
        const {
            currency,
            onCancel,
            isPopupOpened,
            isSaving,
            walletBudget
        } = this.props;
        const { editPopupValue, validationInfo } = this.state;

        return (
            <>
                {this.props.children}
                {isPopupOpened ? (
                    <DayBudgetEditPopup
                        anchor={this}
                        isVisible={isPopupOpened}
                        isInProgress={isSaving}
                        walletBudget={walletBudget}
                        value={editPopupValue}
                        error={this.getErrorText(validationInfo)}
                        currency={currency}
                        onChange={this.onChangeDayBudgetValue}
                        onSave={() => {
                            this.onSaveDayBudgetValue();
                        }}
                        onCancel={onCancel}
                    />
                ) : null}
            </>
        );
    }

    private getErrorText = (
        validationInfo?: ValidationResult
    ): ErrorItemText | undefined => {
        return (
            validationInfo &&
            validationInfo.errors &&
            validationInfo.errors[0] &&
            validationInfo.errors[0].text
        );
    };

    private onChangeDayBudgetValue = (value: RawDayBudgetValue) => {
        this.setState({
            editPopupValue: value,
            validationInfo: undefined
        });
    };

    private async onSaveDayBudgetValue() {
        const { campaignId, saveDayBudget, dailyBudgetLimit } = this.props;
        const { editPopupValue } = this.state;
        const value = this.toDayBudgetValue(editPopupValue);

        const validationResult = validateDayBudgetValue(
            value,
            dailyBudgetLimit
        );

        if (!validationResult.valid) {
            this.setState({
                validationInfo: validationResult
            });
            return;
        }

        saveDayBudget(value, campaignId);
    }

    private toRawDayBudgetValue = (
        value: DayBudgetValue
    ): RawDayBudgetValue => {
        return {
            ...value,
            sum: this.formatSum(value.sum)
        };
    };

    private toDayBudgetValue = (value: RawDayBudgetValue): DayBudgetValue => {
        return {
            ...value,
            sum: parseNumber(value.sum)
        };
    };

    private formatSum = (sum: number): string => {
        return sum ? formatNumber(sum, 2) : '';
    };
}
